/**
 * The cross-sell strip on the order screen.
 *
 * Three rules it follows, all of them about not getting in the way:
 *
 * 1. **It never blocks the order.** The query cannot retry, cannot throw into the tree,
 *    and renders nothing at all when it has nothing useful. A rep who never notices this
 *    panel exists must still be able to raise an order at full speed. That is the same
 *    promise ADR-005 makes on the server, kept again on the client.
 * 2. **It says why.** Every row carries the server's own sentence -- "Frequently bought
 *    with Cola 330ml". A suggestion a rep cannot explain to the shopkeeper in front of
 *    them is a suggestion they will not make.
 * 3. **It measures itself.** What was shown is logged once per distinct panel, and what
 *    was taken is logged on the way to adding the line. Impressions without clicks is the
 *    only honest way to state an accuracy number later.
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { formatMoney } from '@/features/orders/api'
import {
  getRecommendations,
  logRecoEvents,
  type Recommendation,
  type RecoEvent,
} from '@/features/recommendations/api'

export interface CrossSellPanelProps {
  customerId?: string | null
  /** Product ids already on the order. Changing this re-asks. */
  productIds: readonly string[]
  locationId?: string | null
  /** Add the suggestion to the order. Given the whole row: the caller needs its price. */
  onAdd: (item: Recommendation) => void
  limit?: number
}

const HEADINGS: Record<string, string> = {
  model: 'Often bought together',
  customer: 'This customer usually takes',
  popular: 'Selling well',
}

export function CrossSellPanel({
  customerId,
  productIds,
  locationId,
  onAdd,
  limit = 5,
}: CrossSellPanelProps) {
  // Sorted, so re-ordering the same basket does not look like a new question and refetch.
  const key = [...productIds].sort().join(',')

  const query = useQuery({
    queryKey: ['recommendations', customerId ?? '', key, locationId ?? '', limit],
    queryFn: () => getRecommendations({ customerId, productIds, locationId, limit }),
    // One attempt. A retry would spend the rep's network budget on the least important
    // request on the screen, and the fallback answer is already the useful one.
    retry: false,
    staleTime: 60_000,
  })

  const logged = useRef(new Set<string>())
  const data = query.data

  useEffect(() => {
    if (!data || data.items.length === 0) return
    // One impression per distinct panel, not per render. React re-renders for reasons
    // that have nothing to do with the rep looking at anything.
    const shown = `${data.model_version ?? 'none'}:${data.items.map((i) => i.product_id).join(',')}`
    if (logged.current.has(shown)) return
    logged.current.add(shown)

    void logRecoEvents(
      data.items.map((item): RecoEvent => ({
        kind: 'impression',
        strategy: item.strategy,
        product_id: item.product_id,
        source_product_id: item.source_product_id,
        customer_id: customerId ?? null,
        position: item.position,
        score: item.score,
        model_version: data.model_version,
        variant: data.variant,
      })),
    )
  }, [data, customerId])

  const take = (item: Recommendation) => {
    void logRecoEvents([
      {
        kind: 'click',
        strategy: item.strategy,
        product_id: item.product_id,
        source_product_id: item.source_product_id,
        customer_id: customerId ?? null,
        position: item.position,
        score: item.score,
        model_version: data?.model_version ?? null,
        variant: data?.variant ?? null,
      },
    ])
    onAdd(item)
  }

  // Nothing to say, or nothing came back. Render nothing rather than an empty box: a
  // permanent "no suggestions" panel trains people to stop looking at this corner of the
  // screen, and the corner is the whole feature.
  if (query.isError || !data || data.items.length === 0) return null

  return (
    <Card>
      <CardHeader
        title={HEADINGS[data.strategy] ?? HEADINGS.popular}
        description="Suggested from what similar orders contained. Add one, or ignore it."
      />
      <CardBody className="space-y-2">
        <ul className="grid gap-2 tablet:grid-cols-2 desktop:grid-cols-3">
          {data.items.map((item) => (
            <li key={item.product_id}>
              <button
                type="button"
                onClick={() => take(item)}
                className="flex w-full flex-col gap-1 rounded-md border border-slate-200 px-4 py-3 text-left hover:border-brand-500 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800"
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium">{item.name}</span>
                  <span className="text-sm tabular-nums">{formatMoney(item.price)}</span>
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">{item.reason}</span>
              </button>
            </li>
          ))}
        </ul>
        {data.strategy !== 'model' && (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {/* The A/B control arm gets popularity on purpose; adding lines will not change
                it, so the nudge to do so would be a false promise. */}
            {data.fallback_reason === 'ab_control'
              ? 'Based on overall sales.'
              : 'Based on overall sales — add a product to the order for closer matches.'}
          </p>
        )}
      </CardBody>
    </Card>
  )
}
